import { Arc } from "loading-dev";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

const spinnerTone = cva("", {
  variants: {
    tone: {
      default: "text-ink",
      primary: "text-brand",
      secondary: "text-subtle",
      tertiary: "text-subtle",
      interactive: "text-link",
      negative: "text-danger",
      inverse: "text-on-brand",
    },
  },
  defaultVariants: {
    tone: "default",
  },
});

type SpinnerTone = NonNullable<VariantProps<typeof spinnerTone>["tone"]>;

const SPINNER_SIZE_PX = {
  sm: 16,
  md: 32,
  lg: 48,
} as const;

type SpinnerSizeToken = keyof typeof SPINNER_SIZE_PX;

const resolveSpinnerSize = (
  size: number | SpinnerSizeToken | undefined,
  layout: "inline" | "centered",
) => {
  if (typeof size === "number") return size;
  if (size) return SPINNER_SIZE_PX[size];
  return layout === "centered" ? SPINNER_SIZE_PX.md : SPINNER_SIZE_PX.sm;
};

type SpinnerArcProps = {
  pixelSize: number;
  duration?: number;
  tone?: SpinnerTone;
  className?: string;
};

const SpinnerArc = ({
  pixelSize,
  duration,
  tone,
  className,
}: SpinnerArcProps) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center justify-center",
      spinnerTone({ tone }),
      className,
    )}
  >
    <Arc size={pixelSize} duration={duration} />
  </span>
);

type InlineSpinnerProps = {
  layout?: "inline";
  size?: number | SpinnerSizeToken;
  duration?: number;
  tone?: SpinnerTone;
} & Omit<ComponentProps<"span">, "children">;

type CenteredSpinnerProps = {
  layout: "centered";
  size?: number | SpinnerSizeToken;
  duration?: number;
  tone?: SpinnerTone;
  flexHeight?: boolean;
} & Omit<ComponentProps<"div">, "children" | "color">;

type SpinnerProps = InlineSpinnerProps | CenteredSpinnerProps;

function Spinner(props: SpinnerProps) {
  if (props.layout === "centered") {
    const {
      layout: _layout,
      size,
      duration,
      tone,
      flexHeight,
      className,
      role,
      "aria-label": ariaLabel,
      ...rest
    } = props;
    const pixelSize = resolveSpinnerSize(size, "centered");

    return (
      <Stack
        align="start"
        className={cn(
          "w-full items-center justify-center",
          flexHeight ? "flex-1" : "h-full",
          className,
        )}
        role={role ?? (ariaLabel ? "status" : undefined)}
        aria-label={ariaLabel}
        {...rest}
      >
        <span data-slot="spinner" aria-hidden>
          <SpinnerArc pixelSize={pixelSize} duration={duration} tone={tone} />
        </span>
      </Stack>
    );
  }

  const {
    layout: _layout,
    size,
    duration,
    tone,
    className,
    role,
    "aria-label": ariaLabel,
    "aria-hidden": ariaHidden,
    ...rest
  } = props;
  const pixelSize = resolveSpinnerSize(size, "inline");
  const hidden = ariaHidden === true || ariaHidden === "true";

  return (
    <span
      data-slot="spinner"
      role={hidden ? undefined : (role ?? (ariaLabel ? "status" : undefined))}
      aria-label={hidden ? undefined : ariaLabel}
      aria-hidden={ariaHidden}
      className={cn("inline-flex shrink-0 items-center justify-center", className)}
      {...rest}
    >
      <SpinnerArc pixelSize={pixelSize} duration={duration} tone={tone} />
    </span>
  );
}

export {
  Spinner,
  spinnerTone,
  type CenteredSpinnerProps,
  type InlineSpinnerProps,
  type SpinnerProps,
  type SpinnerSizeToken,
  type SpinnerTone,
};
